import styled from "styled-components";
import WithDirection from "@iso/lib/helpers/rtl";

/**
 * Shared wrapper for the Contractor module (list, wizard, profile).
 * Same design system as the redesigned Visitor module: white canvas,
 * stat cards, tab strip with count pills, status/risk badges.
 */
const ContractorPageWrapper = styled.div`
  width: 100%;
  height: 100%;

  .contractor-box {
    padding: 20px 24px;
    border: none;
  }

  /* breadcrumb + header (same recipe as visitor) */
  .ctr-breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #667085;
    margin-bottom: 16px;
    .crumb-sep { color: #98a2b3; }
    .crumb-current { color: #101828; font-weight: 500; }
  }
  .ctr-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }
  .ctr-title {
    font-size: 28px;
    font-weight: 700;
    color: #101828;
    margin: 0 0 2px;
  }
  .ctr-subtitle { font-size: 14px; color: #667085; }
  .ctr-header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
    .property-option { min-width: 240px; max-width: 300px; }
  }
  /* Secondary partner to .btn-dark-green: same geometry, outlined. A plain
     antd Button is 32px and sits visibly short next to the 40px primary, so a
     pair of header actions needs this rather than the default. */
  .btn-outline-green {
    background-color: #ffffff;
    border-color: #d0d5dd;
    color: #101828;
    height: 40px;
    border-radius: 8px;
    font-weight: 500;
    &:hover, &:focus {
      border-color: #0b2a1b;
      color: #0b2a1b;
    }
    &:disabled { opacity: 0.5; }
  }

  .btn-dark-green {
    background-color: #0b2a1b !important;
    border-color: #0b2a1b !important;
    color: #ffffff !important;
    height: 40px;
    border-radius: 8px;
    font-weight: 500;
    &:hover { opacity: 0.85; }
    &:disabled { opacity: 0.5; }
  }

  /* stat cards */
  .stat-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }
  .stat-card {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #ffffff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    padding: 18px 20px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  }
  .stat-icon {
    width: 40px; height: 40px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px; flex-shrink: 0;
    &.icon-total    { background: #f2f4f7; color: #475467; }
    &.icon-approved { background: #ecfdf3; color: #16a34a; }
    &.icon-expiring { background: #fff6ed; color: #ea580c; }
    &.icon-pending  { background: #fef3f2; color: #dc2626; }
  }
  .stat-value { font-size: 22px; font-weight: 700; color: #101828; line-height: 1.2; }
  .stat-label { font-size: 13px; color: #667085; }

  /* tabs */
  .ctr-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid #eaecf0;
    margin-bottom: 16px;
    overflow-x: auto;
  }
  .ctr-tab {
    appearance: none; background: none; border: none;
    border-bottom: 2px solid transparent;
    padding: 10px 14px;
    font-size: 14px; font-weight: 500; color: #667085;
    cursor: pointer; display: flex; align-items: center; gap: 8px;
    white-space: nowrap;
    &:hover { color: #101828; }
    &.active { color: #101828; font-weight: 600; border-bottom-color: #0b2a1b; }
  }
  .tab-count {
    background: #f2f4f7; color: #344054;
    border-radius: 999px; font-size: 12px; font-weight: 600;
    padding: 1px 8px; line-height: 18px;
    &.active { background: #101828; color: #ffffff; }
  }

  /* filter bar */
  .filter-bar {
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 16px; flex-wrap: wrap;
  }
  .ctr-search {
    max-width: 320px; height: 40px; border-radius: 8px;
    border: 1px solid #eaecf0; background: #ffffff; box-shadow: none;
    .ant-input {
      font-size: 14px;
      border: none !important; outline: none !important;
      box-shadow: none !important; background: transparent;
    }
    &.ant-input-affix-wrapper-focused, &:hover { border-color: #0b2a1b; box-shadow: none; }
  }
  .filter-icon { color: #667085; font-size: 16px; }
  .filter-select {
    min-width: 150px;
    .ant-select-selector {
      border-radius: 8px !important;
      height: 40px !important;
      display: flex; align-items: center;
    }
  }

  /* table */
  .ctr-table-wrap {
    border: 1px solid #eaecf0; border-radius: 12px; background: #fff;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
    overflow-x: auto;
  }
  .ctr-table {
    width: 100%; border-collapse: collapse; min-width: 1080px;
    thead th {
      text-align: left; font-size: 12px; font-weight: 600;
      letter-spacing: 0.04em; text-transform: uppercase;
      color: #667085; background: #f9fafb;
      padding: 12px 16px; border-bottom: 1px solid #eaecf0; white-space: nowrap;
    }
    tbody td {
      padding: 14px 16px; border-bottom: 1px solid #f2f4f7;
      vertical-align: middle; font-size: 14px; color: #344054;
    }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: #f9fafb; cursor: pointer; }
  }
  .ctr-cell { display: flex; align-items: center; gap: 12px; }
  .ctr-avatar {
    width: 40px; height: 40px; border-radius: 10px;
    color: #fff; font-weight: 600; font-size: 14px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .ctr-name { font-weight: 600; color: #101828; line-height: 1.3; }
  .ctr-sub { font-size: 12.5px; color: #667085; line-height: 1.4; }

  .category-badge {
    display: inline-block; border-radius: 999px;
    font-size: 12.5px; font-weight: 500; padding: 2px 10px;
    background: #f2f4f7; color: #475467; white-space: nowrap;
  }

  .risk-badge {
    font-size: 13px; font-weight: 600; white-space: nowrap;
    &.risk-low      { color: #026aa2; }
    &.risk-moderate { color: #ca8a04; }
    &.risk-high     { color: #ea580c; }
    &.risk-critical { color: #dc2626; }
  }

  .status-pill {
    display: inline-flex; align-items: center; gap: 6px;
    border-radius: 999px; font-size: 12.5px; font-weight: 500;
    padding: 2px 10px; white-space: nowrap;
    .status-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; }
    &.status-approved    { background: #ecfdf3; color: #067647; .status-dot { background: #17b26a; } }
    &.status-conditional { background: #fffaeb; color: #b54708; .status-dot { background: #f79009; } }
    &.status-pending     { background: #f0f9ff; color: #026aa2; .status-dot { background: #0ba5ec; } }
    &.status-incomplete  { background: #f2f4f7; color: #475467; .status-dot { background: #98a2b3; } }
    &.status-suspended   { background: #fef3f2; color: #b42318; .status-dot { background: #f04438; } }
    &.status-expired     { background: #fef3f2; color: #b42318; .status-dot { background: #f04438; } }
  }

  .alert-cell { font-size: 13px; color: #b54708; white-space: nowrap; }
  .alert-clear { color: #98a2b3; }

  .view-profile-btn {
    border-radius: 999px; font-size: 13px; height: 32px;
    display: inline-flex; align-items: center;
    border-color: #d0d5dd; color: #344054;
    &:hover { border-color: #0b2a1b; color: #0b2a1b; }
  }

  /* ---- wizard ---- */
  .wizard-layout { display: flex; gap: 24px; align-items: flex-start; }
  .wizard-sidebar {
    width: 240px; flex-shrink: 0;
    .wizard-step {
      display: flex; gap: 10px; padding: 12px;
      border-radius: 8px; margin-bottom: 4px;
      cursor: default;
      .step-num {
        width: 24px; height: 24px; border-radius: 50%;
        background: #f2f4f7; color: #475467;
        font-size: 12px; font-weight: 600;
        display: flex; align-items: center; justify-content: center;
        flex-shrink: 0;
      }
      .step-title { font-weight: 600; color: #344054; font-size: 14px; }
      .step-desc { font-size: 12px; color: #98a2b3; }
      &.active { background: #ecfdf3; .step-num { background: #0b2a1b; color: #fff; } }
      &.done .step-num { background: #17b26a; color: #fff; }
    }
    .wizard-progress { margin-top: 16px; padding: 0 12px; font-size: 12px; color: #667085; }
  }
  .wizard-content { flex: 1; min-width: 0; }
  .wizard-section-title { font-size: 20px; font-weight: 700; color: #101828; margin-bottom: 2px; }
  .wizard-section-desc { font-size: 13px; color: #667085; margin-bottom: 16px; }
  .wizard-card {
    background: #fff; border: 1px solid #eaecf0; border-radius: 12px;
    padding: 20px; margin-bottom: 16px;
    .card-heading { font-weight: 600; color: #101828; margin-bottom: 12px; }
  }
  .info-callout {
    background: #eff4ff; border: 1px solid #b2ccff; border-radius: 8px;
    color: #1849a9; font-size: 13px; padding: 10px 14px; margin-bottom: 16px;
  }
  .wizard-footer {
    display: flex; justify-content: space-between; align-items: center;
    border-top: 1px solid #eaecf0; padding-top: 16px; margin-top: 8px;
  }

  .risk-options {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;
    .risk-option {
      border: 1px solid #eaecf0; border-radius: 10px; padding: 12px 14px;
      cursor: pointer;
      .risk-name { font-weight: 600; margin-bottom: 2px; }
      .risk-desc { font-size: 12.5px; color: #667085; }
      &.risk-low .risk-name { color: #026aa2; }
      &.risk-moderate .risk-name { color: #ca8a04; }
      &.risk-high .risk-name { color: #ea580c; }
      &.risk-critical .risk-name { color: #dc2626; }
      &.selected { border-color: #0b2a1b; box-shadow: 0 0 0 2px rgba(11, 42, 27, 0.12); }
    }
  }
  .hazard-grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;
    .hazard-item {
      border: 1px solid #eaecf0; border-radius: 8px; padding: 8px 12px;
      display: flex; align-items: center; gap: 8px; font-size: 13.5px;
      cursor: pointer;
      &.checked { border-color: #0b2a1b; background: #f6fef9; }
    }
  }
  .property-grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;
  }
  .review-grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px 24px;
    .review-label { font-size: 12px; color: #667085; }
    .review-value { font-size: 14px; color: #101828; margin-bottom: 8px;
      &.missing { color: #dc2626; }
    }
  }
  .next-steps-callout {
    background: #fffcf5; border: 1px solid #fec84b; border-radius: 8px;
    color: #93370d; font-size: 13px; padding: 12px 14px;
    ul { margin: 6px 0 0 18px; padding: 0; }
  }

  /* ---- profile ---- */
  .profile-header-card {
    background: #fff; border: 1px solid #eaecf0; border-radius: 12px;
    padding: 20px; margin-bottom: 16px;
  }
  .profile-topline { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
  .profile-name { font-size: 22px; font-weight: 700; color: #101828; }
  .profile-trading { font-size: 14px; color: #667085; }
  .profile-meta { font-size: 13px; color: #667085; margin-top: 4px; display: flex; gap: 12px; flex-wrap: wrap; }
  .verified-tag { color: #067647; font-size: 12.5px; }
  .profile-kpis {
    display: grid; grid-template-columns: repeat(8, 1fr); gap: 10px; margin-top: 16px;
    .kpi {
      border: 1px solid #eaecf0; border-radius: 10px; padding: 10px 12px;
      .kpi-label { font-size: 11.5px; color: #667085; white-space: nowrap; }
      .kpi-value { font-size: 15px; font-weight: 700; color: #101828; margin-top: 2px; }
    }
  }
  .warn-banner {
    background: #fef3f2; border: 1px solid #fecdca; border-radius: 8px;
    color: #b42318; font-size: 13px; padding: 10px 14px; margin: 12px 0 0;
  }
  .profile-tabs {
    display: flex; gap: 4px; border-bottom: 1px solid #eaecf0;
    margin: 16px 0; overflow-x: auto;
  }
  .detail-cards { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .detail-card {
    background: #fff; border: 1px solid #eaecf0; border-radius: 12px;
    .detail-card-head {
      font-weight: 600; color: #101828; padding: 14px 20px;
      border-bottom: 1px solid #f2f4f7; background: #f9fafb;
      border-radius: 12px 12px 0 0;
    }
    .detail-card-body { padding: 16px 20px; }
  }
  .detail-grid {
    display: grid; grid-template-columns: repeat(2, 1fr); gap: 4px 24px;
    .d-label { font-size: 12px; color: #667085; margin-top: 8px; }
    .d-value { font-size: 14px; color: #101828; }
  }
  .restricted-note {
    background: #fffcf5; border: 1px solid #fec84b; border-radius: 8px;
    padding: 12px 14px; color: #93370d; font-size: 13.5px;
  }
  .coming-soon {
    border: 1px dashed #d0d5dd; border-radius: 12px;
    padding: 48px 24px; text-align: center; color: #98a2b3; background: #fff;
  }

  /* ---- register tabs (Phase 2) ---- */
  .register-tab { }
  .register-head {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 14px;
  }
  .register-title { font-size: 16px; font-weight: 600; color: #101828; }
  .register-table-wrap {
    border: 1px solid #eaecf0; border-radius: 12px; background: #fff;
    box-shadow: 0 1px 2px rgba(16,24,40,0.05); overflow-x: auto;
  }
  .register-table {
    width: 100%; border-collapse: collapse; min-width: 720px;
    thead th {
      text-align: left; font-size: 12px; font-weight: 600;
      letter-spacing: 0.04em; text-transform: uppercase; color: #667085;
      background: #f9fafb; padding: 11px 16px; border-bottom: 1px solid #eaecf0;
      white-space: nowrap;
    }
    tbody td {
      padding: 12px 16px; border-bottom: 1px solid #f2f4f7;
      font-size: 13.5px; color: #344054; vertical-align: middle;
    }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: #f9fafb; }
  }
  .reg-main { font-weight: 600; color: #101828; }
  .reg-sub { font-size: 12px; color: #667085; }
  .reg-muted { color: #98a2b3; }

  .expiry-pill, .status-pill-sm {
    display: inline-block; border-radius: 999px; font-size: 11.5px;
    font-weight: 600; padding: 1px 8px; text-transform: capitalize; white-space: nowrap;
  }
  .expiry-active   { background: #ecfdf3; color: #067647; }
  .expiry-expiring { background: #fffaeb; color: #b54708; }
  .expiry-expired  { background: #fef3f2; color: #b42318; }
  .pill-green { background: #ecfdf3; color: #067647; }
  .pill-amber { background: #fffaeb; color: #b54708; }
  .pill-blue  { background: #f0f9ff; color: #026aa2; }
  .pill-red   { background: #fef3f2; color: #b42318; }
  /* Neutral: "nothing here", as opposed to a state that is good or bad.
     Used for a worker with no AirAgri login — not a problem, just an absence. */
  .pill-grey  { background: #f2f4f7; color: #475467; }

  .reg-form-item { margin-bottom: 14px; }
  .reg-file-chip {
    display: flex; align-items: center; gap: 8px;
    border: 1px solid #eaecf0; border-radius: 8px; padding: 6px 10px;
    font-size: 13px;
  }

  @media only screen and (max-width: 1100px) {
    .stat-cards { grid-template-columns: repeat(2, 1fr); }
    .profile-kpis { grid-template-columns: repeat(4, 1fr); }
  }
  @media only screen and (max-width: 640px) {
    .contractor-box { padding: 14px; }
    .stat-cards, .detail-cards, .risk-options, .hazard-grid, .property-grid, .review-grid { grid-template-columns: 1fr; }
    .profile-kpis { grid-template-columns: repeat(2, 1fr); }
    .wizard-layout { flex-direction: column; }
    .wizard-sidebar { width: 100%; }
  }
`;

export default WithDirection(ContractorPageWrapper);
